import { createNoise2D, createNoise3D } from "simplex-noise";
import { createSeededRng, hashCoords } from "../utils/math";
import { CHUNK_SIZE, SEA_LEVEL } from "../world/constants";
import { BlockType } from "../world/block-registry";
import { getBlockIndex } from "../world/chunk";
import { getBiome, BIOME_CONFIGS, type BiomeConfig } from "./biomes";

/**
 * Generate terrain blocks for a single chunk section.
 * Deterministic: same seed + coords = same terrain.
 * Uses biome system for variety.
 */
export function generateChunkTerrain(
  cx: number,
  cy: number,
  cz: number,
  seed: number
): Uint16Array {
  const rng = createSeededRng(seed);
  const noise2D = createNoise2D(rng);
  const noise3D = createNoise3D(createSeededRng(seed + 1));

  // Biome noise (very large scale — smooth transitions)
  const tempNoise = createNoise2D(createSeededRng(seed + 100));
  const humidNoise = createNoise2D(createSeededRng(seed + 200));

  const blocks = new Uint16Array(CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE);
  const chunkWorldY = cy * CHUNK_SIZE;

  for (let x = 0; x < CHUNK_SIZE; x++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      const wx = cx * CHUNK_SIZE + x;
      const wz = cz * CHUNK_SIZE + z;

      // Determine biome for this column
      const temperature = tempNoise(wx * 0.003, wz * 0.003);
      const humidity = humidNoise(wx * 0.003, wz * 0.003);
      const biome = BIOME_CONFIGS[getBiome(temperature, humidity)];

      const surfaceHeight = getSurfaceHeight(noise2D, wx, wz, biome);

      for (let y = 0; y < CHUNK_SIZE; y++) {
        const wy = chunkWorldY + y;
        const idx = getBlockIndex(x, y, z);
        blocks[idx] = getBlockAt(wy, surfaceHeight, noise3D, wx, wy, wz, biome);
      }
    }
  }

  // Decoration pass: trees
  decorateTrees(blocks, cx, cy, cz, seed, noise2D, tempNoise, humidNoise);

  // Decoration pass: structures
  decorateStructures(blocks, cx, cy, cz, seed, noise2D, tempNoise, humidNoise);

  return blocks;
}

function getSurfaceHeight(
  noise2D: ReturnType<typeof createNoise2D>,
  wx: number,
  wz: number,
  biome: BiomeConfig
): number {
  // Multi-octave terrain shaped by biome amplitude
  const large = noise2D(wx * 0.008, wz * 0.008);
  const medium = noise2D(wx * 0.03, wz * 0.03) * 0.5;
  const detail = noise2D(wx * 0.1, wz * 0.1) * 0.15;

  const combined = large + medium + detail;

  return Math.floor(SEA_LEVEL + biome.baseOffset + combined * biome.amplitude);
}

function getBlockAt(
  wy: number,
  surfaceHeight: number,
  noise3D: ReturnType<typeof createNoise3D>,
  wx: number,
  _wy: number,
  wz: number,
  biome: BiomeConfig
): BlockType {
  if (wy === 0) return BlockType.BEDROCK;

  // Underground
  if (wy < surfaceHeight - 4) {
    // Caves
    const caveNoise = noise3D(wx * 0.05, wy * 0.05, wz * 0.05);
    if (caveNoise > 0.6) return BlockType.AIR;

    // Ores (depth-based)
    if (wy < 16) {
      const oreNoise = noise3D(wx * 0.15, wy * 0.15, wz * 0.15);
      if (oreNoise > 0.85) return BlockType.DIAMOND_ORE;
    }
    if (wy < 32) {
      const oreNoise = noise3D(wx * 0.12 + 100, wy * 0.12, wz * 0.12);
      if (oreNoise > 0.8) return BlockType.GOLD_ORE;
    }
    if (wy < 64) {
      const oreNoise = noise3D(wx * 0.1 + 200, wy * 0.1, wz * 0.1);
      if (oreNoise > 0.75) return BlockType.IRON_ORE;
    }
    if (wy < 128) {
      const oreNoise = noise3D(wx * 0.1 + 300, wy * 0.1, wz * 0.1);
      if (oreNoise > 0.7) return BlockType.COAL_ORE;
    }

    return BlockType.STONE;
  }

  // Sub-surface
  if (wy < surfaceHeight) return biome.subSurfaceBlock;

  // Surface layer
  if (wy === surfaceHeight) {
    if (surfaceHeight < SEA_LEVEL - 2) return BlockType.SAND; // beach
    return biome.surfaceBlock;
  }

  // Water fill below sea level
  if (wy <= SEA_LEVEL && wy > surfaceHeight) return BlockType.WATER;

  return BlockType.AIR;
}

// --- TREE DECORATION ---

function decorateTrees(
  blocks: Uint16Array,
  cx: number,
  cy: number,
  cz: number,
  seed: number,
  noise2D: ReturnType<typeof createNoise2D>,
  tempNoise: ReturnType<typeof createNoise2D>,
  humidNoise: ReturnType<typeof createNoise2D>
) {
  const chunkWorldY = cy * CHUNK_SIZE;

  for (let x = 2; x < CHUNK_SIZE - 2; x++) {
    for (let z = 2; z < CHUNK_SIZE - 2; z++) {
      const wx = cx * CHUNK_SIZE + x;
      const wz = cz * CHUNK_SIZE + z;

      // Get biome tree chance
      const temperature = tempNoise(wx * 0.003, wz * 0.003);
      const humidity = humidNoise(wx * 0.003, wz * 0.003);
      const biome = BIOME_CONFIGS[getBiome(temperature, humidity)];

      if (biome.treeChance === 0) continue;

      // Deterministic placement
      const treeHash = Math.abs(hashCoords(wx, seed + 7, wz));
      const chance = (treeHash % 1000) / 1000;
      if (chance > biome.treeChance) continue;

      const surfaceHeight = getSurfaceHeight(noise2D, wx, wz, biome);

      // Only above sea level, on biome surface
      if (surfaceHeight <= SEA_LEVEL) continue;

      const localSurfaceY = surfaceHeight - chunkWorldY;
      if (localSurfaceY < 0 || localSurfaceY >= CHUNK_SIZE - 8) continue;

      const surfaceIdx = getBlockIndex(x, localSurfaceY, z);
      if (blocks[surfaceIdx] !== biome.surfaceBlock) continue;

      const trunkHeight = 4 + (treeHash % 3);
      placeTree(blocks, x, localSurfaceY + 1, z, trunkHeight);
    }
  }
}

function placeTree(
  blocks: Uint16Array,
  x: number,
  baseY: number,
  z: number,
  trunkHeight: number
) {
  // Trunk
  for (let dy = 0; dy < trunkHeight; dy++) {
    const y = baseY + dy;
    if (y >= CHUNK_SIZE) break;
    setBlockSafe(blocks, x, y, z, BlockType.WOOD);
  }

  // Leaves canopy
  const canopyStart = baseY + trunkHeight - 1;
  const canopyRadius = 2;

  for (let dy = -1; dy <= 2; dy++) {
    const y = canopyStart + dy;
    if (y < 0 || y >= CHUNK_SIZE) continue;

    const radius = dy === 2 || dy === -1 ? 1 : canopyRadius;

    for (let dx = -radius; dx <= radius; dx++) {
      for (let dz = -radius; dz <= radius; dz++) {
        const lx = x + dx;
        const lz = z + dz;
        if (lx < 0 || lx >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

        if (Math.abs(dx) === radius && Math.abs(dz) === radius && dy !== 0) continue;

        const idx = getBlockIndex(lx, y, lz);
        if (blocks[idx] === BlockType.AIR) {
          blocks[idx] = BlockType.LEAVES;
        }
      }
    }
  }
}

// --- STRUCTURE DECORATION ---

function decorateStructures(
  blocks: Uint16Array,
  cx: number,
  cy: number,
  cz: number,
  seed: number,
  noise2D: ReturnType<typeof createNoise2D>,
  tempNoise: ReturnType<typeof createNoise2D>,
  humidNoise: ReturnType<typeof createNoise2D>
) {
  const chunkWorldY = cy * CHUNK_SIZE;

  const structHash = Math.abs(hashCoords(cx, seed + 42, cz));

  // Get biome at chunk center
  const centerWx = cx * CHUNK_SIZE + 8;
  const centerWz = cz * CHUNK_SIZE + 8;
  const temperature = tempNoise(centerWx * 0.003, centerWz * 0.003);
  const humidity = humidNoise(centerWx * 0.003, centerWz * 0.003);
  const biome = BIOME_CONFIGS[getBiome(temperature, humidity)];

  const chance = (structHash % 1000) / 1000;
  if (chance > biome.structureChance) return;

  const sx = 4 + (structHash % 6);
  const sz = 4 + ((structHash >> 4) % 6);
  const wx = cx * CHUNK_SIZE + sx;
  const wz = cz * CHUNK_SIZE + sz;

  const surfaceHeight = getSurfaceHeight(noise2D, wx, wz, biome);
  if (surfaceHeight <= SEA_LEVEL) return;

  const localSurfaceY = surfaceHeight - chunkWorldY;
  if (localSurfaceY < 0 || localSurfaceY >= CHUNK_SIZE - 7) return;

  // Flatness check
  const corners = [
    getSurfaceHeight(noise2D, wx - 2, wz - 2, biome),
    getSurfaceHeight(noise2D, wx + 2, wz - 2, biome),
    getSurfaceHeight(noise2D, wx - 2, wz + 2, biome),
    getSurfaceHeight(noise2D, wx + 2, wz + 2, biome),
  ];
  const maxDiff = Math.max(...corners) - Math.min(...corners);
  if (maxDiff > 2) return;

  const structType = structHash % 3;
  if (structType === 0) {
    placeSmallHouse(blocks, sx, localSurfaceY + 1, sz);
  } else if (structType === 1) {
    placeRuin(blocks, sx, localSurfaceY + 1, sz);
  } else {
    placeWell(blocks, sx, localSurfaceY + 1, sz);
  }
}

function placeSmallHouse(blocks: Uint16Array, x: number, baseY: number, z: number) {
  const w = 5, h = 4, d = 5;

  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      for (let dz = 0; dz < d; dz++) {
        const lx = x + dx - 2;
        const ly = baseY + dy;
        const lz = z + dz - 2;
        if (!inBounds(lx, ly, lz)) continue;

        const isWall = dx === 0 || dx === w - 1 || dz === 0 || dz === d - 1;
        const isRoof = dy === h - 1;
        const isDoor = dx === 2 && dz === 0 && dy < 2;

        if (isRoof) {
          setBlockSafe(blocks, lx, ly, lz, BlockType.PLANKS);
        } else if (isDoor) {
          setBlockSafe(blocks, lx, ly, lz, BlockType.AIR);
        } else if (isWall) {
          setBlockSafe(blocks, lx, ly, lz, BlockType.COBBLESTONE);
        } else {
          setBlockSafe(blocks, lx, ly, lz, BlockType.AIR);
        }
      }
    }
  }
}

function placeRuin(blocks: Uint16Array, x: number, baseY: number, z: number) {
  const positions = [[0, 0], [3, 0], [0, 3], [3, 3], [1, 1]];

  for (const [dx, dz] of positions) {
    const pillarHeight = 1 + (Math.abs(hashCoords(x + dx, baseY, z + dz)) % 3);
    for (let dy = 0; dy < pillarHeight; dy++) {
      const lx = x + dx - 1;
      const ly = baseY + dy;
      const lz = z + dz - 1;
      if (!inBounds(lx, ly, lz)) continue;
      setBlockSafe(blocks, lx, ly, lz, BlockType.COBBLESTONE);
    }
  }
}

function placeWell(blocks: Uint16Array, x: number, baseY: number, z: number) {
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      const lx = x + dx;
      const lz = z + dz;

      for (let dy = 0; dy < 2; dy++) {
        const ly = baseY + dy;
        if (!inBounds(lx, ly, lz)) continue;

        const isEdge = Math.abs(dx) === 1 || Math.abs(dz) === 1;
        if (isEdge) {
          setBlockSafe(blocks, lx, ly, lz, BlockType.COBBLESTONE);
        } else {
          setBlockSafe(blocks, lx, ly, lz, BlockType.WATER);
        }
      }
    }
  }
}

// --- HELPERS ---

function setBlockSafe(blocks: Uint16Array, x: number, y: number, z: number, blockId: BlockType) {
  if (!inBounds(x, y, z)) return;
  blocks[getBlockIndex(x, y, z)] = blockId;
}

function inBounds(x: number, y: number, z: number): boolean {
  return x >= 0 && x < CHUNK_SIZE && y >= 0 && y < CHUNK_SIZE && z >= 0 && z < CHUNK_SIZE;
}
